<script setup lang="ts">
    import { onMounted, ref, computed, shallowRef, toRefs } from 'vue';
    import { storeToRefs } from 'pinia';
    import { useRuntimeConfig } from 'nuxt/app';

    import { useBookingStore } from '@/stores/booking';
    import { useModalStore } from '@/stores/modals';
    import { useAvailabilityStore } from '~/stores/booking-availability';
    import { useErrorStore } from '~/stores/error';

    import { excludeTestGyms, ILocation, sortGymsAlphabetically } from '@/helpers/gymClusters';
    import { fetchAndStoreAvailability } from '@/helpers/fetchData';
    import { fetchAllLocations } from '@/helpers/fetchAllData';
    import {
        findNearestLocation,
        getNearByGymLocations,
        getUserLocation,
    } from '~/helpers/geoLocation';

    import LocationDetailBlock from './LocationDetailBlock.vue';
    import AvailabilityTimeSlot from './AvailabilityDateTimeSlot.vue';
    import LocationModal from './LocationModal.vue';
    import PricingPanelInfo from './PricingPanelInfo.vue';
    import { nearByGymLocationSearchRadius, visibleNearByGymsLimit } from '~/helpers/constants.js';
    import GiftVoucherInput from './GiftVoucherInput.vue';
    import { fetchStandardPackages } from '~/helpers/fetchStandardPackages.js';
    import { Campaign } from '../enums.js';

    const emit = defineEmits(['toggle-loading']);
    const props = defineProps({
        isSubmitted: Boolean,
        locationId: {
            type: String,
            default: '',
        },
    });

    const { isSubmitted, locationId } = toRefs(props);

    const bookingStore = useBookingStore();
    const availabilityStore = useAvailabilityStore();
    const errorStore = useErrorStore();
    const runtimeConfig = useRuntimeConfig();

    const { state: availability } = storeToRefs(availabilityStore);
    const allGyms = shallowRef<ILocation[]>([]);
    const isLoader = ref(true);
    const modalStore = useModalStore();

    const isLocationModalOpen = computed(() => modalStore.state.locationModal);

    onMounted(async () => {
        try {
            isLoader.value = true;

            const [gyms, userLocation] = await Promise.all([
                fetchAllLocations(),
                getUserLocation(),
            ]);

            if (!Array.isArray(gyms) || !gyms.length) {
                console.warn('No gyms found');
                return;
            }

            const sortedGyms = sortGymsAlphabetically(gyms);
            const isDev = runtimeConfig.public.environment === 'dev';
            const finalGyms = excludeTestGyms(sortedGyms, userLocation, isDev);
            allGyms.value = finalGyms;

            const requestedGym = locationId.value ? finalGyms.find((gym) => gym.location_id === locationId.value) : undefined;
            if (locationId.value && !requestedGym) {
                console.warn(`No gym found for location_id ${locationId.value}`);
            }
            const nearestGym = requestedGym ?? findNearestLocation(userLocation, allGyms.value);

            if (!nearestGym) {
                throw new Error('No valid locations found');
            }
            const nearByLocations = getNearByGymLocations(nearestGym, gyms, nearByGymLocationSearchRadius);

            bookingStore.updateBookingState({
                display_name: nearestGym.location_name,
                currency: nearestGym.currency,
                address: nearestGym.address,
                booking_business: nearestGym.booking_business,
                service_id: nearestGym.service_id,
                timezone: nearestGym.timezone,
                timezone_name: nearestGym.timezone_name,
                near_by_location: nearByLocations.slice(0, visibleNearByGymsLimit),
                maps_url: nearestGym.maps_url,
            });

            if (nearestGym.is_pricing_panel_enabled) await fetchStandardPackages(nearestGym.location_id);

            await fetchAndStoreAvailability(
                bookingStore,
                availabilityStore,
                errorStore,
                runtimeConfig,
                nearestGym.location_name,
                true
            );
        } catch (err) {
            console.error(err);
        } finally {
            emit('toggle-loading', false);
            isLoader.value = false;
        }
    });
</script>

<template>
    <div 
        v-if="!isLoader"
        class="bg-white p-5 md:px-8 md:pt-8 md:pb-16 rounded-3xl"
    >
        <!-- INPUT GIFT VOUCHER CODE -->
         <div v-if="bookingStore.state.utm_campaign === Campaign.giftOfHealth2026">
             <GiftVoucherInput 
             :isSubmitted="isSubmitted" 
             />
         </div>

        <!-- SELECT LOCATION BLOCK -->
        <LocationDetailBlock 
            :allGyms="allGyms" 
        />

         <!-- PRICING PANEL -->
            <div v-if="bookingStore.state.utm_campaign !== Campaign.giftOfHealth2026">
                <PricingPanelInfo v-if="bookingStore.state.is_pricing_panel_enabled && !bookingStore.state.is_loader" />
            </div>

        <!-- AVAILABILITY TIME SLOTS -->
        <AvailabilityTimeSlot
            :availability="availability"
            :isSubmitted="isSubmitted" 
        />
    </div>

    <!-- CHANGE LOCATION MODAL -->
    <div
        v-if="isLocationModalOpen"
        class="bg-black/50 flex justify-center items-center fixed top-0 bottom-0 left-0 right-0 z-50 overflow-hidden"
    >
        <div class="bg-[#efede7] rounded-3xl max-h-[calc(100vh-120px)] w-full md:max-w-[592px] mx-5 my-16 p-5 md:p-8 flex flex-col">
            <LocationModal :allGyms="allGyms" />
        </div>
    </div>
</template>
